import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, MapPin, Briefcase, Mail, MessageCircle, Edit, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const counties = ["Sonoma", "Napa", "Marin", "Lake", "Mendocino"];

const commonCertifications = [
  "OSHA 10-Hour",
  "OSHA 30-Hour",
  "CDL Class A",
  "CDL Class B",
  "EPA 608 Certification",
  "Forklift Operator",
  "Scissor Lift Certified",
  "First Aid/CPR",
  "Welding Certification",
  "Electrical License",
  "Plumbing License",
  "HVAC License",
  "Journeyman License",
  "Apprentice Card"
];

export default function ProfilePage({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [isConnected, setIsConnected] = useState(false);

  const isOwnProfile = user.id === id;

  useEffect(() => {
    fetchProfile();
    if (!isOwnProfile) {
      checkConnection();
    }
  }, [id]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error("Failed to fetch profile", error);
    } finally {
      setLoading(false);
    }
  };

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const connected = response.data.some(
        (conn) => conn.connected_user_id === id || conn.user_id === id
      );
      setIsConnected(connected);
    } catch (error) {
      console.error("Failed to check connection", error);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/users/profile`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully");
      setEditDialogOpen(false);
      fetchProfile();
      
      const updatedUser = { ...user, ...editData };
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleConnect = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/connections`, { connected_user_id: id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Connection added");
      setIsConnected(true);
    } catch (error) {
      toast.error("Failed to connect");
    }
  };

  const handleMessage = () => {
    navigate(`/messages?userId=${id}`);
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!profile) {
    return <div className="text-center py-12">Profile not found</div>;
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/community")}
        className="mb-4"
        data-testid="back-to-community-button"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Community
      </Button>

      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-teal-50">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            <Avatar className="h-24 w-24" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)' }}>
              <AvatarFallback className="text-white text-3xl font-bold">
                {getInitials(profile.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <CardTitle className="text-3xl mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                {profile.name}
              </CardTitle>
              <div className="flex flex-wrap gap-2 items-center">
                {profile.trade_focus && (
                  <Badge variant="outline" className="text-base">{profile.trade_focus}</Badge>
                )}
                <Badge className={profile.role === "graduate" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}>
                  {profile.role === "graduate" ? "Graduate" : "Student"}
                </Badge>
                {profile.grad_year && (
                  <Badge variant="outline">Class of {profile.grad_year}</Badge>
                )}
              </div>
            </div>
            {isOwnProfile ? (
              <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="edit-profile-button" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Profile
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Profile</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={editData.name || ""}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        data-testid="edit-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Trade Focus</Label>
                      <Input
                        value={editData.trade_focus || ""}
                        onChange={(e) => setEditData({ ...editData, trade_focus: e.target.value })}
                        data-testid="edit-trade-focus-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>County</Label>
                      <Select
                        value={editData.county || ""}
                        onValueChange={(value) => setEditData({ ...editData, county: value })}
                      >
                        <SelectTrigger data-testid="edit-county-select">
                          <SelectValue placeholder="Select county" />
                        </SelectTrigger>
                        <SelectContent>
                          {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {profile.role === "graduate" && (
                      <div className="space-y-2">
                        <Label>Graduation Year</Label>
                        <Input
                          type="number"
                          value={editData.grad_year || ""}
                          onChange={(e) => setEditData({ ...editData, grad_year: parseInt(e.target.value) })}
                          data-testid="edit-grad-year-input"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Bio</Label>
                      <Textarea
                        value={editData.bio || ""}
                        onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                        rows={4}
                        data-testid="edit-bio-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number (Optional)</Label>
                      <Input
                        type="tel"
                        value={editData.phone || ""}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        placeholder="e.g., (707) 555-0123"
                        data-testid="edit-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contact Preference</Label>
                      <Input
                        value={editData.contact_preference || ""}
                        onChange={(e) => setEditData({ ...editData, contact_preference: e.target.value })}
                        placeholder="e.g., Open to networking"
                        data-testid="edit-contact-preference-input"
                      />
                    </div>
                    
                    <div className="border-t pt-4 space-y-4">
                      <h3 className="font-semibold text-gray-900">Certifications & Licenses</h3>
                      <p className="text-sm text-gray-600">Add your professional certifications</p>
                      
                      <div className="space-y-2">
                        <Label>Select Certifications</Label>
                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                          {commonCertifications.map((cert) => (
                            <div key={cert} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`cert-${cert}`}
                                checked={(editData.certifications || []).includes(cert)}
                                onChange={(e) => {
                                  const certs = editData.certifications || [];
                                  if (e.target.checked) {
                                    setEditData({ ...editData, certifications: [...certs, cert] });
                                  } else {
                                    setEditData({ ...editData, certifications: certs.filter(c => c !== cert) });
                                  }
                                }}
                                className="h-4 w-4 text-teal-600 rounded"
                              />
                              <label htmlFor={`cert-${cert}`} className="text-sm text-gray-700 cursor-pointer">
                                {cert}
                              </label>
                            </div>
                          ))}
                        </div>
                        {editData.certifications && editData.certifications.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {editData.certifications.map((cert) => (
                              <Badge key={cert} variant="outline" className="bg-teal-50 text-teal-700">
                                {cert}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="border-t pt-4 space-y-4">
                      <h3 className="font-semibold text-gray-900">Privacy Settings</h3>
                      <p className="text-sm text-gray-600">Control what information is visible to other users</p>
                      
                      <div className="flex items-center justify-between py-2">
                        <div className="space-y-0.5">
                          <Label htmlFor="show-email">Show Email Address</Label>
                          <p className="text-sm text-gray-500">Let other users see your email</p>
                        </div>
                        <Switch
                          id="show-email"
                          checked={editData.show_email || false}
                          onCheckedChange={(checked) => setEditData({ ...editData, show_email: checked })}
                          data-testid="show-email-toggle"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between py-2">
                        <div className="space-y-0.5">
                          <Label htmlFor="show-phone">Show Phone Number</Label>
                          <p className="text-sm text-gray-500">Let other users see your phone</p>
                        </div>
                        <Switch
                          id="show-phone"
                          checked={editData.show_phone || false}
                          onCheckedChange={(checked) => setEditData({ ...editData, show_phone: checked })}
                          data-testid="show-phone-toggle"
                        />
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-900">
                          <strong>Privacy Note:</strong> Admins can always see your contact information. Other users will see "Hidden (privacy settings)" if you keep these off.
                        </p>
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleUpdateProfile}
                      className="w-full"
                      data-testid="save-profile-button"
                      style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
                    >
                      Save Changes
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <div className="flex gap-2">
                {!isConnected && (
                  <Button
                    onClick={handleConnect}
                    variant="outline"
                    data-testid="connect-button"
                  >
                    Connect
                  </Button>
                )}
                <Button
                  onClick={handleMessage}
                  data-testid="message-button"
                  style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Message
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`mailto:compliance@ngta.com?subject=Report User: ${profile.name}&body=User ID: ${profile.id}%0D%0A%0D%0APlease describe the issue:`, '_blank')}
                  className="text-red-600 hover:text-red-700 border-red-300"
                  data-testid="report-profile-button"
                >
                  Report
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} />
              <div>
                <p className="font-medium text-gray-900">Email</p>
                <p className="text-gray-600">
                  {profile.email}
                  {!isOwnProfile && profile.email === "Hidden (privacy settings)" && (
                    <span className="text-xs text-gray-500 block mt-1">Private</span>
                  )}
                </p>
              </div>
            </div>
            {profile.phone && (
              <div className="flex items-start gap-3">
                <svg className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <p className="font-medium text-gray-900">Phone</p>
                  <p className="text-gray-600">{profile.phone}</p>
                </div>
              </div>
            )}
            {profile.county && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-1" style={{ color: '#4ECDC4' }} />
                <div>
                  <p className="font-medium text-gray-900">County</p>
                  <p className="text-gray-600">{profile.county}</p>
                </div>
              </div>
            )}
            {profile.trade_focus && (
              <div className="flex items-start gap-3">
                <Briefcase className="h-5 w-5 mt-1" style={{ color: '#FF6B6B' }} />
                <div>
                  <p className="font-medium text-gray-900">Trade Focus</p>
                  <p className="text-gray-600">{profile.trade_focus}</p>
                </div>
              </div>
            )}
            {profile.grad_year && (
              <div className="flex items-start gap-3">
                <GraduationCap className="h-5 w-5 mt-1" style={{ color: '#FF6B6B' }} />
                <div>
                  <p className="font-medium text-gray-900">Graduation Year</p>
                  <p className="text-gray-600">{profile.grad_year}</p>
                </div>
              </div>
            )}
          </div>

          {profile.bio && (
            <div className="border-t pt-6">
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>About</h3>
              <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
            </div>
          )}

          {profile.certifications && profile.certifications.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="text-xl font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <GraduationCap className="h-6 w-6 text-teal-600" />
                Certifications & Licenses
              </h3>
              <div className="flex flex-wrap gap-2">
                {profile.certifications.map((cert) => (
                  <Badge key={cert} variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-sm px-3 py-1">
                    {cert}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {profile.contact_preference && (
            <div className="border-t pt-6">
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Contact Preference</h3>
              <p className="text-gray-700">{profile.contact_preference}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}